/**
 * CashFlowDashboardPage
 *
 * Finance / PIJAR cash flow dashboard. Combines filter (with inline tweaks),
 * KPI summary cards, trend chart, side charts and the hierarchical detail table.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Box, Grid, VStack, Heading, Text, useToast } from '@chakra-ui/react';
import Navbar from '../../components/Navbar';
import api from '../../auth/apiClient';
import useCashFlow from '../hooks/useCashFlow';
import CashFlowFilter, { CashFlowViewMode } from '../components/CashFlowFilter';
import CashFlowSummaryCards from '../components/CashFlowSummaryCards';
import CashFlowTrendChart from '../components/CashFlowTrendChart';
import CashFlowSideCharts from '../components/CashFlowSideCharts';
import CashFlowDetailTable from '../components/CashFlowDetailTable';
import { exportToExcel } from '../utils/exportToExcel';
import { CashFlowFilters, CompanyOption, TweaksSettings } from '../types';
import { getMonthName } from '../utils';

const DEFAULT_TWEAKS: TweaksSettings = {
    summaryLayout: 'period-cards',
    chartStyle: 'bars',
    showCharts: true,
};

const CashFlowDashboardPage: React.FC = () => {
    const toast = useToast();
    const { detail, summary, trend, weekly, loading, filters, setFilters, fetchData } = useCashFlow();
    const [viewMode, setViewMode] = useState<CashFlowViewMode>('monthly');
    const [companies, setCompanies] = useState<CompanyOption[]>([]);
    const [tweaks, setTweaks] = useState<TweaksSettings>(DEFAULT_TWEAKS);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const response = await api.get('/inspeksi/options');
                setCompanies(response.data?.companies || []);
            } catch (error) {
                console.error('Error fetching companies:', error);
            }
        };
        fetchCompanies();
    }, []);

    const handleApplyFilters = useCallback(
        (next: CashFlowFilters) => {
            setFilters(next);
            fetchData(next);
        },
        [setFilters, fetchData],
    );

    const companyName = companies.find((c) => c.id === filters.company_id)?.name || `Company ${filters.company_id}`;

    const handleExport = useCallback(() => {
        if (!detail || detail.length === 0) {
            toast({ title: 'Tidak ada data', status: 'warning', duration: 3000, isClosable: true });
            return;
        }
        try {
            exportToExcel(detail, { companyName, year: filters.year, month: filters.month });
            toast({ title: 'Berhasil', description: 'Data diekspor ke Excel', status: 'success', duration: 3000, isClosable: true });
        } catch (error: any) {
            toast({ title: 'Gagal ekspor', description: error.message || 'Terjadi kesalahan', status: 'error', duration: 5000, isClosable: true });
        }
    }, [detail, companyName, filters.year, filters.month, toast]);

    return (
        <Box minH="100vh" display="flex" flexDirection="column" bg="gray.50">
            <Navbar />
            <Box flex="1" p={{ base: 4, md: 6 }} zIndex={1}>
                <VStack spacing={6} align="stretch">
                    <VStack align="start" spacing={0}>
                        <Heading size="lg" color="customGreen.500">
                            Cash Flow Dashboard
                        </Heading>
                        <Text fontSize="sm" color="gray.500">
                            {companyName} · {getMonthName(filters.month)} {filters.year}
                        </Text>
                    </VStack>

                    <CashFlowFilter
                        filters={filters}
                        onApplyFilters={handleApplyFilters}
                        onExport={handleExport}
                        viewMode={viewMode}
                        onViewModeChange={setViewMode}
                        tweaks={tweaks}
                        onTweaksChange={setTweaks}
                    />

                    <CashFlowSummaryCards
                        summary={summary}
                        loading={loading}
                        layout={tweaks.summaryLayout}
                        companyName={companyName}
                    />

                    {tweaks.showCharts && (
                        <Grid templateColumns={{ base: '1fr', lg: '1.7fr 1fr' }} gap={4}>
                            <CashFlowTrendChart
                                trend={trend}
                                loading={loading}
                                chartStyle={tweaks.chartStyle}
                            />
                            <CashFlowSideCharts
                                summary={summary}
                                detail={detail}
                                loading={loading}
                            />
                        </Grid>
                    )}

                    <CashFlowDetailTable
                        detail={detail}
                        weekly={weekly}
                        summary={summary}
                        viewMode={viewMode}
                        loading={loading}
                    />
                </VStack>
            </Box>
        </Box>
    );
};

export default CashFlowDashboardPage;
